import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { onValue, ref } from 'firebase/database'

import { db, tref } from '../lib/firebase.js'
import { fmtVsPar, getPlayStyle, headToHeadStatus, matchPlayResult, parsFor, rankTeams } from '../lib/scoring.js'
import { aggregateBuyers, computePayouts } from '../lib/data.js'
import CLUB from '../config/club.js'
import PageNav from '../components/PageNav.jsx'

// Public tournament hub. Single page everyone scans to follow the
// tournament — players, families, friends. No login. Three sections:
//
//   /hub?t={tournamentId}
//
//   1. 🏆 Live Leaderboard — same data as /leaderboard, embedded compact
//   2. ⏰ Tee Times — latest configured day (today / tomorrow)
//   3. 💰 Calcutta — only when enabled, top 3 leaders + owners + payouts
//
export default function HubApp() {
  const [params] = useSearchParams()
  const tournamentId = params.get('t')

  const [tournament, setTournament] = useState(null)
  const [error, setError] = useState('')
  const [showWelcome, setShowWelcome] = useState(() => {
    // Play the welcome splash once per browser session — NOT every time the
    // user comes back to the hub (e.g. after Photo Submit / Photo Wall), which
    // would replay the splash instead of showing the hub they expect.
    try { return sessionStorage.getItem('hubWelcomeShown') !== '1' } catch { return true }
  })

  useEffect(() => {
    if (!tournamentId) {
      setError('Missing tournament ID. Scan the Tournament Hub QR.')
      return
    }
    const off = onValue(tref(`tournaments/${tournamentId}`), (snap) => {
      const v = snap.val()
      if (!v) setError('Tournament not found.')
      else setTournament(v)
    })
    return () => off()
  }, [tournamentId])

  return (
    <div className="min-h-full" style={{ backgroundColor: CLUB.backgroundColor }}>
      {showWelcome ? (
        <HubWelcome
          tournamentName={tournament?.info?.name}
          onDone={() => {
            try { sessionStorage.setItem('hubWelcomeShown', '1') } catch {}
            setShowWelcome(false)
          }}
        />
      ) : null}
      <PageNav back home={false} tone="gold" />
      {tournamentId ? (
        <a
          href={`/photos?t=${tournamentId}`}
          className="absolute top-3 right-3 z-30 rounded-xl px-4 py-2.5 text-sm font-extrabold uppercase tracking-wider transition active:scale-95 whitespace-nowrap"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)', color: CLUB.accentColor, border: `1.5px solid ${CLUB.accentColor}88` }}>
          📸 Photo Submit
        </a>
      ) : null}
      <div className="max-w-3xl mx-auto px-4 pt-14 pb-10">
        <HubHeader tournament={tournament} />
        {error ? (
          <ErrorCard message={error} />
        ) : !tournament ? (
          <p className="text-white/70 text-center mt-8">Loading…</p>
        ) : (
          <>
            <a
              href={`/photowall?t=${tournamentId}`}
              className="block w-full text-center rounded-xl px-4 py-3 mb-4 font-extrabold uppercase tracking-widest transition active:scale-[0.98]"
              style={{ backgroundColor: CLUB.primaryColor, color: CLUB.accentColor, border: `1px solid ${CLUB.accentColor}66` }}>
              🖼️ Photos &amp; Gallery
            </a>
            <LeaderboardSection tournament={tournament} />
            <TeeTimesSection tournament={tournament} />
            <CalcuttaSection tournamentId={tournamentId} tournament={tournament} />
            <PhotosSection tournamentId={tournamentId} tournament={tournament} />
          </>
        )}
      </div>
    </div>
  )
}

// Full-screen welcome splash shown when the hub first opens. The cream PBCC
// logo spins in (same 1080° spin-and-settle as the scorecard welcome), then
// "Welcome to {tournament} — LIVE" fades up, holds, and the whole screen fades
// out to reveal the hub. Tap anywhere to skip.
function HubWelcome({ tournamentName, onDone }) {
  const [exiting, setExiting] = useState(false)

  useEffect(() => {
    // Cinzel — same display font the scorecard welcome uses. Injected once.
    const id = 'cinzel-welcome-font'
    if (!document.getElementById(id)) {
      const link = document.createElement('link')
      link.id = id
      link.rel = 'stylesheet'
      link.href = 'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700;800&display=swap'
      document.head.appendChild(link)
    }
    // Spin (~3.2s) → message fades up → hold, then fade the screen out.
    const t = window.setTimeout(() => {
      setExiting(true)
      window.setTimeout(onDone, 550)
    }, 5500)
    return () => window.clearTimeout(t)
  }, [onDone])

  const skip = () => { setExiting(true); window.setTimeout(onDone, 400) }

  return (
    <div
      onClick={skip}
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center px-6 text-center"
      style={{
        backgroundColor: 'var(--ge-primary)',
        opacity: exiting ? 0 : 1,
        transition: 'opacity 0.5s ease',
        cursor: 'pointer',
        overflow: 'hidden',
      }}>
      <style>{`
        @keyframes hubLogoIn {
          0%   { opacity: 0; transform: scale(0) rotate(0deg); }
          100% { opacity: 1; transform: scale(1) rotate(1080deg); }
        }
        @keyframes hubFadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes hubPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }
      `}</style>

      <img
        src={CLUB.logoLight}
        alt="PBCC"
        style={{
          width: 'min(340px, 80vw)', objectFit: 'contain', marginBottom: 20,
          animation: 'hubLogoIn 3s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s both',
        }}
      />
      <div
        className="text-sm sm:text-base font-bold uppercase"
        style={{ fontFamily: 'Cinzel, serif', color: 'var(--ge-primary)', letterSpacing: '0.3em', animation: 'hubFadeUp 0.6s ease 3s backwards' }}>
        Welcome to
      </div>
      <div
        className="text-3xl sm:text-4xl font-extrabold uppercase mt-2 leading-tight"
        style={{ fontFamily: 'Cinzel, serif', color: '#FFFFFF', animation: 'hubFadeUp 0.6s ease 3.3s backwards' }}>
        {tournamentName || 'PBCC 4-Ball'}
      </div>
      <div
        className="mt-5 inline-flex items-center gap-2.5 text-xl sm:text-2xl font-extrabold uppercase"
        style={{ fontFamily: 'Cinzel, serif', color: 'var(--ge-primary)', letterSpacing: '0.35em', animation: 'hubFadeUp 0.6s ease 3.6s backwards' }}>
        <span style={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#E23B3B', display: 'inline-block', animation: 'hubPulse 1.4s ease-in-out infinite' }} />
        Live
      </div>
    </div>
  )
}

function HubHeader({ tournament }) {
  return (
    <div className="text-center pt-2 pb-4">
      <div className="inline-block" style={{ marginBottom: '12px' }}>
        <img src={CLUB.logoLight} alt={CLUB.name} style={{ height: '185px', maxWidth: '86vw', objectFit: 'contain', display: 'block' }} />
      </div>
      <h1
        className="text-xl sm:text-2xl uppercase tracking-widest"
        style={{ color: CLUB.accentColor, fontFamily: 'Cinzel, serif', fontWeight: 600 }}>
        🏌️ Tournament Hub
      </h1>
      {tournament?.info?.name ? (
        <p className="text-white/85 mt-1 font-bold text-base">{tournament.info.name}</p>
      ) : null}
      {tournament?.info?.date ? (
        <p className="text-white/65 text-xs">
          {tournament.info.course || ''} · {tournament.info.date}
        </p>
      ) : null}
    </div>
  )
}

function ErrorCard({ message }) {
  return (
    <div className="bg-white rounded-2xl p-5 mt-6 text-center shadow-xl">
      <div className="text-3xl mb-2">⚠️</div>
      <p className="text-sm text-gray-700">{message}</p>
    </div>
  )
}

// ── Section 1: Leaderboard ─────────────────────────────────────────────────
function LeaderboardSection({ tournament }) {
  const flights = useMemo(
    () => Object.entries(tournament.flights || {}).map(([id, v]) => ({ id, ...v })),
    [tournament.flights],
  )
  const pars = parsFor(tournament)

  return (
    <SectionShell title="🏆 Live Leaderboard">
      {flights.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">Flights will appear here once configured.</p>
      ) : (
        <div className="space-y-3">
          {flights.map((f) => (
            <FlightLeaderboardCard
              key={f.id}
              flight={f}
              pars={pars}
              playStyle={getPlayStyle(tournament, f)}
            />
          ))}
        </div>
      )}
    </SectionShell>
  )
}

function FlightLeaderboardCard({ flight, pars, playStyle }) {
  const teams = useMemo(
    () => Object.entries(flight.teams || {}).map(([id, v]) => ({ id, ...v })),
    [flight.teams],
  )
  if (playStyle === 'match') {
    return <MatchLeaderboard flight={flight} teams={teams} />
  }
  const ranked = rankTeams(teams, false, pars)
  return (
    <div className="rounded-xl bg-white border border-gray-200 overflow-hidden">
      <div className="bg-green-deep text-white px-3 py-2 flex justify-between items-center">
        <h3 className="font-extrabold text-sm">{flight.info?.name}</h3>
        <span className="text-[10px] uppercase tracking-widest opacity-80">Stroke</span>
      </div>
      {ranked.length === 0 ? (
        <p className="text-xs text-gray-500 text-center py-3">No teams yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-gray-500 border-b">
            <tr>
              <th className="text-left px-3 py-1.5 w-8">#</th>
              <th className="text-left px-2 py-1.5">Team</th>
              <th className="text-right px-2 py-1.5">Thru</th>
              <th className="text-right px-3 py-1.5">vs Par</th>
            </tr>
          </thead>
          <tbody>
            {ranked.map((r, i) => (
              <tr key={r.id} className={`border-b last:border-0 ${i === 0 ? 'bg-yellow-50' : ''}`}>
                <td className="px-3 py-1.5 font-extrabold text-base text-green-deep">{i + 1}</td>
                <td className="px-2 py-1.5">
                  <div className="font-bold text-sm leading-tight">{r.info?.name}</div>
                  <div className="text-[11px] text-gray-500 truncate">{(r.info?.players || []).join(' · ')}</div>
                </td>
                <td className="px-2 py-1.5 text-right">{r.thru}</td>
                <td className="px-3 py-1.5 text-right font-extrabold">{fmtVsPar(r.vsPar)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function MatchLeaderboard({ flight, teams }) {
  const matches = useMemo(() => {
    const byId = Object.fromEntries(teams.map((t) => [t.id, t]))
    const seen = new Set()
    const out = []
    for (const t of teams) {
      if (seen.has(t.id)) continue
      const oppId = t.info?.opponentId
      if (oppId && byId[oppId] && oppId !== t.id) {
        seen.add(t.id); seen.add(oppId)
        const opp = byId[oppId]
        const h2h = headToHeadStatus(t, opp)
        const gap = h2h.myWins - h2h.oppWins
        let teamA, teamB, leaderGap, aWins, bWins
        if (gap >= 0) { teamA = t; teamB = opp; leaderGap = gap; aWins = h2h.myWins; bWins = h2h.oppWins }
        else          { teamA = opp; teamB = t; leaderGap = -gap; aWins = h2h.oppWins; bWins = h2h.myWins }
        const result = matchPlayResult({ myWins: aWins, oppWins: bWins, played: h2h.played })
        out.push({ key: `${t.id}-${opp.id}`, teamA, teamB, leaderGap, played: h2h.played, result })
      } else {
        seen.add(t.id)
        out.push({ key: `solo-${t.id}`, teamA: t, teamB: null, leaderGap: 0, played: 0, result: null })
      }
    }
    // Finals first, then by lead size, then holes played.
    out.sort((a, b) => {
      const af = a.result?.final ? 1 : 0
      const bf = b.result?.final ? 1 : 0
      if (af !== bf) return bf - af
      return (b.leaderGap - a.leaderGap) || (b.played - a.played)
    })
    return out
  }, [teams])
  return (
    <div className="rounded-xl bg-white border border-gray-200 overflow-hidden">
      <div className="bg-green-deep text-white px-3 py-2 flex justify-between items-center">
        <h3 className="font-extrabold text-sm">{flight.info?.name}</h3>
        <span className="text-[10px] uppercase tracking-widest opacity-80">Match</span>
      </div>
      {matches.length === 0 ? (
        <p className="text-xs text-gray-500 text-center py-3">No matches yet.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {matches.map((m, i) => <MatchSummaryRow key={m.key} index={i} match={m} />)}
        </ul>
      )}
    </div>
  )
}

function MatchSummaryRow({ index, match }) {
  const { teamA, teamB, leaderGap, played, result } = match
  if (!teamB) {
    return (
      <li className="px-3 py-2 text-amber-700 text-sm">
        <b>{index + 1}.</b> {teamA.info?.name} — no opponent
      </li>
    )
  }
  const isFinal = !!result?.final
  const halved = isFinal && result?.winner === 'tie'
  const allSquare = leaderGap === 0
  const notStarted = played === 0
  let status, statusColor
  if (isFinal) {
    status = halved ? `FINAL · Halved` : `FINAL · ${teamA.info?.name} wins ${result.resultText}`
    statusColor = halved ? CLUB.accentColor : '#0a5a2a'
  } else if (notStarted) { status = 'Not started'; statusColor = '#6B7280' }
  else if (allSquare) { status = `ALL SQUARE THRU ${played}`; statusColor = CLUB.accentColor }
  else { status = `${leaderGap} UP THRU ${played}`; statusColor = '#0a5a2a' }
  return (
    <li className="px-3 py-2 text-sm flex justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="font-bold text-green-deep truncate">
          {isFinal && !halved ? '🏆 ' : '▲ '}{teamA.info?.name}
        </div>
        <div className="text-[11px] text-gray-500 truncate">{teamB.info?.name}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-xs font-extrabold whitespace-nowrap" style={{ color: statusColor }}>{status}</div>
      </div>
    </li>
  )
}

// ── Section 2: Tee Times ───────────────────────────────────────────────────
//
// "Tomorrow" rule: show the highest day that has wave1 OR wave2 flights
// configured. That naturally gives "today" before round starts and the
// admin's freshly-generated next-day after each wave finishes.
function TeeTimesSection({ tournament }) {
  const teeTimes = tournament.teeTimes || {}
  const flightsById = useMemo(() => tournament.flights || {}, [tournament.flights])

  const dayToShow = useMemo(() => {
    const days = Object.keys(teeTimes).map(Number).filter(Number.isFinite).sort((a, b) => a - b)
    let latest = null
    for (const d of days) {
      const dayData = teeTimes[d] || {}
      const w1Set = dayData?.waves?.wave1?.flights && Object.keys(dayData.waves.wave1.flights).length > 0
      const w2Set = dayData?.waves?.wave2?.flights && Object.keys(dayData.waves.wave2.flights).length > 0
      if (w1Set || w2Set) latest = d
    }
    return latest
  }, [teeTimes])

  if (!dayToShow) {
    return (
      <SectionShell title="⏰ Tee Times">
        <p className="text-sm text-gray-600 text-center py-4">
          ⏳ Tomorrow's tee times will be posted after today's round — check back soon.
        </p>
      </SectionShell>
    )
  }

  const dayData = teeTimes[dayToShow] || {}
  const dayLabel = `Day ${dayToShow}`
  return (
    <SectionShell title={`⏰ Tee Times — ${dayLabel}`}>
      <div className="space-y-3">
        {['wave1', 'wave2'].map((wk, idx) => {
          const wave = dayData?.waves?.[wk] || {}
          const flightIds = Object.keys(wave.flights || {})
          if (flightIds.length === 0) return null
          return (
            <WaveCard
              key={wk}
              title={`Wave ${idx + 1}`}
              startTime={wave.startTime}
              flightIds={flightIds}
              flightsById={flightsById}
              assignments={wave.assignments || {}}
            />
          )
        })}
        {!dayData?.waves?.wave1?.flights && !dayData?.waves?.wave2?.flights ? (
          <p className="text-sm text-gray-600 text-center py-3">
            ⏳ Tomorrow's tee times will be posted after today's round — check back soon.
          </p>
        ) : null}
      </div>
    </SectionShell>
  )
}

function WaveCard({ title, startTime, flightIds, flightsById, assignments }) {
  // Build a list of matches per flight, sorted by flight ID then by hole.
  const flightSections = useMemo(() => {
    const sortedFlightIds = flightIds.slice().sort()
    return sortedFlightIds.map((fid) => {
      const flight = flightsById[fid]
      if (!flight) return null
      const teams = Object.entries(flight.teams || {})
      const teamMap = Object.fromEntries(teams)
      const seen = new Set()
      const matches = []

      // Pass 1: opponentId pairs
      for (const [tid, t] of teams) {
        if (seen.has(tid)) continue
        const oppId = t?.info?.opponentId
        if (oppId && teamMap[oppId] && !seen.has(oppId) && oppId !== tid) {
          seen.add(tid); seen.add(oppId)
          const opp = teamMap[oppId]
          matches.push({
            teamIds: [tid, oppId],
            label: `${t.info?.name || tid} vs ${opp.info?.name || oppId}`,
            hole: assignments[tid] || assignments[oppId] || '',
          })
        }
      }
      // Pass 2: sequential fallback
      const leftover = teams.filter(([tid]) => !seen.has(tid))
      for (let i = 0; i < leftover.length; i += 2) {
        const [tidA, tA] = leftover[i]
        const next = leftover[i + 1]
        if (next) {
          const [tidB, tB] = next
          matches.push({
            teamIds: [tidA, tidB],
            label: `${tA?.info?.name || tidA} vs ${tB?.info?.name || tidB}`,
            hole: assignments[tidA] || assignments[tidB] || '',
          })
        } else {
          matches.push({
            teamIds: [tidA],
            label: tA?.info?.name || tidA,
            hole: assignments[tidA] || '',
          })
        }
      }

      // Sort by hole label so the wave sheet reads top-down by hole.
      matches.sort((a, b) => holeRank(a.hole) - holeRank(b.hole))

      return {
        flightId: fid,
        flightName: flight.info?.name || fid,
        matches,
      }
    }).filter(Boolean)
  }, [flightIds, flightsById, assignments])

  if (flightSections.length === 0) return null
  return (
    <div className="rounded-xl bg-white border border-gray-200 overflow-hidden">
      <div className="bg-green-deep text-white px-3 py-2 flex justify-between items-center">
        <h3 className="font-extrabold text-sm">{title}</h3>
        <span className="text-[11px] font-bold opacity-90">{startTime || '—'} Shotgun</span>
      </div>
      <div className="px-3 py-2 space-y-2">
        {flightSections.map((sec) => (
          <div key={sec.flightId}>
            <div className="text-[11px] uppercase tracking-widest font-extrabold text-green-deep mt-1.5 mb-1">
              {sec.flightName}
            </div>
            <ul className="space-y-0.5">
              {sec.matches.map((m, i) => (
                <li key={i} className="flex justify-between gap-2 text-sm">
                  <span className="truncate flex-1">
                    <b className="text-gray-400 mr-1">{i + 1}.</b>
                    {m.label}
                  </span>
                  <span className="font-mono font-bold text-green-deep shrink-0">
                    {m.hole ? `Hole ${m.hole}` : '—'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  )
}

// Sort helper: "1A" before "1B" before "2", before "5A", etc.
function holeRank(label) {
  if (!label) return 999
  const m = String(label).match(/^(\d+)([A-Z]?)/)
  if (!m) return 999
  return Number(m[1]) * 10 + (m[2] ? m[2].charCodeAt(0) - 64 : 0)
}

// ── Section 3: Calcutta ────────────────────────────────────────────────────
function CalcuttaSection({ tournamentId, tournament }) {
  const settings = tournament?.calcutta?.settings || {}
  const bids = tournament?.calcutta?.bids || {}
  const paid = tournament?.calcutta?.paid || {}
  const enabled = !!settings.enabled
  const [showAll, setShowAll] = useState(false)

  // Total pot computation (mirrors PotSummaryCard logic in CalcuttaTab).
  const totalPot = useMemo(() => {
    let sum = 0
    for (const b of Object.values(bids)) {
      sum += Number(b.bidAmount) || 0
      if (settings.buyBackEnabled && b.buyBack) sum += Number(b.buyBackCost) || 0
    }
    return sum
  }, [bids, settings.buyBackEnabled])

  const payoutStructure = Array.isArray(settings.payoutStructure) ? settings.payoutStructure : []
  const payouts = useMemo(() => computePayouts(totalPot, payoutStructure), [totalPot, payoutStructure])

  // Top finishers across all flights, ranked by gross (lower better). Falls
  // back to vsPar when gross is missing.
  const topFinishers = useMemo(() => {
    const flights = Object.entries(tournament.flights || {}).map(([id, v]) => ({ id, ...v }))
    const pars = parsFor(tournament)
    const all = []
    for (const f of flights) {
      const teams = Object.entries(f.teams || {}).map(([id, v]) => ({ id, ...v }))
      const ranked = rankTeams(teams, false, pars)
      ranked.forEach((r) => all.push({ ...r, flightName: f.info?.name || f.id }))
    }
    all.sort((a, b) => {
      if (a.gross == null && b.gross == null) return 0
      if (a.gross == null) return 1
      if (b.gross == null) return -1
      return a.gross - b.gross
    })
    return all
  }, [tournament])

  const buyersAll = useMemo(() => aggregateBuyers(bids, paid), [bids, paid])

  if (!enabled) return null

  // Pair top N finishers with their owners + payouts for the standings card.
  const standings = payouts.map((p, i) => {
    const team = topFinishers[i]
    const owner = team ? bids[team.id] : null
    return { ...p, team, ownerName: owner?.buyerName || null }
  })
  const medals = ['🥇', '🥈', '🥉']

  return (
    <SectionShell title="💰 Calcutta">
      <div className="rounded-xl bg-white border border-gray-200 p-4">
        <div className="flex justify-between items-end gap-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">Total Pot</div>
            <div className="text-2xl font-extrabold text-green-deep">${totalPot.toLocaleString()}</div>
          </div>
          <div className="text-right text-xs text-gray-700">
            {payouts.map((p, i) => (
              <div key={i}>
                {ordinal(p.place)} → <b className="text-green-deep">${p.amount.toLocaleString()}</b>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-3">
        <div className="text-[11px] uppercase tracking-widest font-extrabold mb-2" style={{ color: CLUB.accentColor }}>
          Current Standings
        </div>
        <div className="space-y-2">
          {standings.map((s, i) => (
            <div key={i} className="rounded-xl bg-white border border-gray-200 p-3 flex justify-between items-center gap-3">
              <div className="min-w-0">
                <div className="font-extrabold flex items-center gap-2 text-sm">
                  <span className="text-base">{medals[i] || ordinal(s.place)}</span>
                  <span className="truncate">{s.team?.info?.name || '—'}</span>
                </div>
                <div className="text-[11px] text-gray-500 truncate">
                  {s.team?.flightName || ''}{s.ownerName ? ` · Owner: ${s.ownerName}` : ' · No owner'}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Potential</div>
                <div className="font-extrabold text-green-deep">${s.amount.toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {buyersAll.length > 0 ? (
        <div className="mt-3">
          <button
            onClick={() => setShowAll((v) => !v)}
            className="text-xs underline text-gray-600 font-semibold">
            {showAll ? '▴ Hide' : '▾ Show'} all teams &amp; owners ({buyersAll.length} buyer{buyersAll.length === 1 ? '' : 's'})
          </button>
          {showAll ? (
            <div className="mt-2 rounded-xl bg-white border border-gray-200 divide-y divide-gray-100">
              {buyersAll.map((b) => (
                <div key={b.buyerKey} className="px-3 py-2 flex justify-between items-baseline text-sm">
                  <span className="font-semibold truncate">{b.buyerName}</span>
                  <span className="text-xs text-gray-500 shrink-0 ml-2">
                    {b.teams.length} team{b.teams.length === 1 ? '' : 's'} · ${b.totalOwed.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </SectionShell>
  )
}

// ── Section 4: Photos (last 6 approved + link to wall) ────────────────────
function PhotosSection({ tournamentId, tournament }) {
  const photos = useMemo(() => {
    const all = tournament?.photos || {}
    return Object.entries(all)
      .map(([id, v]) => ({ id, ...v }))
      .filter((p) => p.status === 'approved')
      .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
      .slice(0, 6)
  }, [tournament])
  const [openIdx, setOpenIdx] = useState(-1)
  const submissionsEnabled = tournament?.photoSettings?.enabled !== false

  // Only render the section once at least one approved photo exists.
  if (photos.length === 0) return null
  return (
    <SectionShell title="📸 Photos">
      <div className="grid grid-cols-3 gap-2">
        {photos.map((p, i) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setOpenIdx(i)}
            className="aspect-square overflow-hidden rounded-lg bg-gray-100 hover:opacity-90 transition">
            {p.type === 'video' ? (
              <video src={p.url} className="w-full h-full object-cover" muted />
            ) : (
              <img src={p.thumbnailUrl || p.url} alt={p.caption || ''} className="w-full h-full object-cover" loading="lazy" />
            )}
          </button>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2 justify-center">
        {submissionsEnabled ? (
          <a
            href={`/photos?t=${tournamentId}`}
            className="inline-block rounded-lg px-3 py-1.5 text-xs font-extrabold uppercase tracking-widest"
            style={{ backgroundColor: CLUB.primaryColor, color: CLUB.accentColor }}>
            📸 Share a Photo
          </a>
        ) : null}
        <a
          href={`/photowall?t=${tournamentId}`}
          target="_blank"
          rel="noopener"
          className="inline-block rounded-lg px-3 py-1.5 text-xs font-extrabold uppercase tracking-widest border-2"
          style={{ borderColor: CLUB.accentColor, color: CLUB.accentColor }}>
          🖼️ Full photo wall
        </a>
      </div>

      {openIdx >= 0 && photos[openIdx] ? (
        <PhotoLightbox photo={photos[openIdx]} onClose={() => setOpenIdx(-1)} />
      ) : null}
    </SectionShell>
  )
}

function PhotoLightbox({ photo, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        {photo.type === 'video' ? (
          <video src={photo.url} className="w-full max-h-[70vh] object-contain rounded-xl" controls autoPlay />
        ) : (
          <img src={photo.url} alt={photo.caption || ''} className="w-full max-h-[70vh] object-contain rounded-xl" />
        )}
        <div className="mt-3 text-white text-sm text-center">
          {photo.caption ? <div className="font-bold mb-1">{photo.caption}</div> : null}
          <div className="text-white/70 text-xs">
            {photo.submittedBy || 'Guest'}{photo.hole != null ? ` · Hole ${photo.hole}` : ''}
          </div>
        </div>
        <button
          onClick={onClose}
          className="mt-3 mx-auto block text-white/80 text-xs underline">
          Close
        </button>
      </div>
    </div>
  )
}

function SectionShell({ title, children }) {
  return (
    <section className="bg-white/95 rounded-2xl shadow-xl p-4 mt-5">
      <h2 className="text-base font-extrabold uppercase tracking-widest text-green-deep mb-3">{title}</h2>
      {children}
    </section>
  )
}

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}
